import { createSlice } from '@reduxjs/toolkit';
import { pushDynamicTab } from '../utils/dynamicTabs';

const SLICE_VERSION = '1.2';
const DEFAULT_STATUS_FILTER = 'New';
const DEFAULT_LIST_VIEW_STATE = {
  currentPage: 1,
  itemsPerPage: 50,
};

const getAllTabs = (state) => [...state.defaultTabs, ...state.dynamicTabs];

// Record the tab the user is leaving so a later tab closure can return to it.
const pushTabHistory = (state, tabId) => {
  if (!tabId) return;
  state.tabHistory = state.tabHistory.filter((id) => id !== tabId);
  state.tabHistory.push(tabId);
};

export const initialState = {
  _version: SLICE_VERSION,

  defaultTabs: [
    { id: 'orders', name: 'Orders', contentType: 'orders' },
  ],

  dynamicTabs: [],
  activeTab: 0,
  // Stack of previously active tab ids, most-recent-last — lets removeTab send the
  // user back to whichever tab/page they were actually viewing before, not tab 0.
  tabHistory: [],
  statusFilter: DEFAULT_STATUS_FILTER,
  listViewState: {
    "orders-incoming": { ...DEFAULT_LIST_VIEW_STATE },
  },
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,

  reducers: {
    addTab: (state, action) => {
      const payload = action.payload || {};

      const tab = {
        id: payload.id || `dyn-${Date.now()}`,
        name: payload.name || `New Order ${state.dynamicTabs.length + 1}`,
        isDynamic: true,
        createdAt: Date.now(),

        // ⭐ Store ALL fields for matching
        contentType: payload.contentType || null,
        orderNumber: payload.orderNumber || null,
        orderOfficeCode: payload.orderOfficeCode || null,
        searchQuery: payload.searchQuery || null,
        appointmentData: payload.appointmentData || null,
      };

      const prevActiveId = getAllTabs(state)[state.activeTab]?.id;
      const added = pushDynamicTab(state, tab, state.defaultTabs.length);
      if (added) pushTabHistory(state, prevActiveId);
    },

    removeTab: (state, action) => {
      const { tabIndex } = action.payload;
      if (tabIndex < state.defaultTabs.length) return;

      const tabsBefore = getAllTabs(state);
      const removedId = tabsBefore[tabIndex]?.id;
      const activeId = tabsBefore[state.activeTab]?.id;
      const wasActive = tabIndex === state.activeTab;

      const dynamicIndex = tabIndex - state.defaultTabs.length;
      state.dynamicTabs.splice(dynamicIndex, 1);
      state.tabHistory = state.tabHistory.filter((id) => id !== removedId);

      const tabsAfter = getAllTabs(state);

      if (!wasActive) {
        // The tab the user was viewing wasn't the one closed — stay on it, just
        // recompute its index since removing an earlier tab shifts the array.
        const newIndex = tabsAfter.findIndex((t) => t.id === activeId);
        state.activeTab = newIndex >= 0 ? newIndex : Math.min(state.activeTab, tabsAfter.length - 1);
        return;
      }

      // The active tab itself was closed — return to the last tab/page the user
      // was actually on, walking back through history until one still exists.
      while (state.tabHistory.length) {
        const candidateId = state.tabHistory.pop();
        const idx = tabsAfter.findIndex((t) => t.id === candidateId);
        if (idx >= 0) {
          state.activeTab = idx;
          return;
        }
      }

      // No usable history — fall back to the default Orders tab.
      state.activeTab = 0;
    },

    setActiveTab: (state, action) => {
      const { id, index } = action.payload;
      const tabs = getAllTabs(state);

      if (id) {
        const idx = tabs.findIndex((t) => t.id === id);
        if (idx !== -1 && idx !== state.activeTab) {
          pushTabHistory(state, tabs[state.activeTab]?.id);
          state.activeTab = idx;
        }
        return;
      }

      if (index != null) {
        const total = state.defaultTabs.length + state.dynamicTabs.length;
        if (index >= 0 && index < total && index !== state.activeTab) {
          pushTabHistory(state, tabs[state.activeTab]?.id);
          state.activeTab = index;
        }
      }
    },

    resetTabs: (state) => {
      state.dynamicTabs = [];
      state.activeTab = 0;
      state.tabHistory = [];
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setListViewState: (state, action) => {
      const { stateKey = "orders-incoming", ...rest } = action.payload || {};
      const existing = state.listViewState[stateKey] || {
        ...DEFAULT_LIST_VIEW_STATE,
      };
      state.listViewState[stateKey] = {
        ...existing,
        ...rest,
      };
    },
  },
});

export const { addTab, removeTab, setActiveTab, resetTabs, setStatusFilter, setListViewState } =
  ordersSlice.actions;

export default ordersSlice.reducer;